import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { CyclingEvent, EventCategory, haversineDistance, CATEGORY_LABELS, CATEGORY_COLORS, displayBundesland, distanceShortLabel, toExternalUrl } from '@radevents/shared';
import eventService from '../services/eventService';
import toast from 'react-hot-toast';
import { formatDayLong } from '../utils/format';

const categoryLabel = (c: EventCategory) => CATEGORY_LABELS[c] || c;
// Gleiche Kategoriefarben wie im oeffentlichen Frontend (CATEGORY_COLORS),
// als Badge: Text in Kategoriefarbe auf ~12% getoenter Flaeche.
const categoryColor = (c: EventCategory) => {
  const hex = CATEGORY_COLORS[c] ?? '#6A6A6A';
  return { bg: `${hex}1F`, text: hex };
};

const Dashboard: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  usePageTitle('Dashboard');
  const [favorites, setFavorites] = useState<CyclingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    eventService.getMyFavorites()
      .then(setFavorites)
      .catch(() => toast.error('Favoriten konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  const handleRemoveFavorite = async (eventId: number) => {
    try {
      await eventService.removeFavorite(eventId);
      setFavorites(prev => prev.filter(e => e.id !== eventId));
      toast.success('Favorit entfernt');
    } catch {
      toast.error('Aktion fehlgeschlagen');
    }
  };

  const handleShare = async (event: CyclingEvent) => {
    const url = `${window.location.origin}/?event=${event.id}`;
    const text = `Schau dir dieses Event an: ${event.title} in ${displayBundesland(event.bundesland)}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, text, url });
      } catch (err) {
        // Ignore abort errors
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success((t) => (
          <div className="flex flex-col gap-2">
            <span>Link kopiert!</span>
            <a 
              href={`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold underline"
              onClick={() => toast.dismiss(t.id)}
            >
              Über WhatsApp teilen
            </a>
          </div>
        ), { duration: 5000 });
      } catch {
        toast.error('Fehler beim Kopieren');
      }
    }
  };

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 5) return 'Noch wach?';
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    if (hour < 22) return 'Guten Abend';
    return 'Späte Schicht';
  };

  return (
    <div className="p-6 lg:p-10">
      {/* Welcome */}
      <div className="mb-10 animate-fade-up">
        <p className="text-sm font-medium uppercase tracking-widest mb-2"
          style={{ color: 'var(--accent-warm)', letterSpacing: '0.1em' }}>
          {greeting()}
        </p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight"
          style={{ color: 'var(--text-primary)', letterSpacing: '-0.025em' }}>
          {user?.firstName}
        </h1>
        <p className="mt-2 text-lg" style={{ color: 'var(--text-muted)' }}>
          {favorites.length > 0
            ? `Du hast ${favorites.length} Event${favorites.length !== 1 ? 's' : ''} auf deiner Merkliste.`
            : 'Du hast noch keine Events auf deiner Merkliste.'}
        </p>
      </div>

      {/* Favorites */}
      <div className="mb-6">
        <h2 className="text-xl font-bold tracking-tight mb-4" style={{ color: 'var(--text-primary)' }}>
          <svg className="inline-block h-5 w-5 mr-2 -mt-0.5" viewBox="0 0 24 24" fill="#ef4444" stroke="#ef4444" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
          </svg>
          Meine Merkliste
        </h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--accent-warm)' }} />
        </div>
      ) : favorites.length === 0 ? (
        <div className="rounded-xl p-10 text-center"
          style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
          <svg className="h-12 w-12 mx-auto mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            style={{ color: 'var(--text-placeholder)' }} strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
          </svg>
          <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            Noch keine Favoriten. Markiere Events auf der Startseite mit einem Herz!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {favorites.map((event, i) => {
            const catColors = categoryColor(event.category);
            return (
              <div key={event.id}
                className={`rounded-xl p-6 card-interactive animate-fade-up stagger-${Math.min(i + 1, 6)}`}
                style={{
                  background: 'var(--surface-bg)',
                  border: '1px solid var(--surface-border)',
                }}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold tracking-tight truncate"
                      style={{ color: 'var(--text-primary)' }}>
                      {event.title}
                    </h3>
                    {event.cancelled && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">
                        ABGESAGT
                      </span>
                    )}
                    {event.fullyBooked && !event.cancelled && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">
                        AUSGEBUCHT
                      </span>
                    )}
                    {event.tentative && !event.cancelled && !event.fullyBooked && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700">
                        VORLÄUFIG
                      </span>
                    )}
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {event.organizer}
                    </p>
                  </div>
                  <div className="flex gap-1 ml-2 flex-shrink-0">
                    <button
                      onClick={() => handleShare(event)}
                      className="p-1 rounded transition-colors"
                      title="Event teilen"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0-10.628a2.25 2.25 0 110 4.5 2.25 2.25 0 010-4.5zm0 10.628a2.25 2.25 0 110 4.5 2.25 2.25 0 010-4.5z" />
                      </svg>
                    </button>
                    <button onClick={() => handleRemoveFavorite(event.id)}
                      className="p-1 rounded transition-colors flex-shrink-0"
                      title="Favorit entfernen">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#ef4444" stroke="#ef4444" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex px-2 py-0.5 text-xs font-bold rounded-md"
                    style={{ background: catColors.bg, color: catColors.text }}>
                    {categoryLabel(event.category)}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {formatDayLong(event.eventDate)}
                  </span>
                </div>

                <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{displayBundesland(event.bundesland)}</span>
                  {' \u2014 '}{event.address}
                  {user?.latitude != null && user?.longitude != null && event.latitude != null && event.longitude != null && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold"
                      style={{ background: 'var(--page-bg)', color: 'var(--text-muted)', border: '1px solid var(--surface-border)' }}>
                      ~{Math.round(haversineDistance(user.latitude, user.longitude, event.latitude, event.longitude))} km entfernt
                    </span>
                  )}
                </p>

                {event.distances.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {event.distances.map(d => (
                      <span key={d.id}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                        style={{
                          background: 'var(--page-bg)',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--surface-border)',
                        }}>
                        {distanceShortLabel(d)}
                        {d.elevationMeters > 0 && (
                          <span className="ml-1" style={{ color: 'var(--text-muted)' }}>
                            {d.elevationMeters} hm
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                )}

                {event.organizerUrl && (
                  <a href={toExternalUrl(event.organizerUrl)} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium mt-3 hover:opacity-80 transition-opacity"
                    style={{ color: 'var(--accent-warm)' }}>
                    Zur Veranstalter-Seite
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
